import { styled } from '@linaria/react'
import React, { type FC, type ReactNode } from 'react'
import CheckInCircleIcon from '../svgr/CheckInCircle'
import { colors } from '../styles/colors'

export const OptionButton: FC<{
  value: number
  checked: boolean
  onChange: (value: number) => void
  disabled?: boolean
  children?: ReactNode
  className?: string
  isRightAnswer?: boolean | null
}> = ({
  value,
  checked,
  onChange,
  disabled = false,
  children,
  className,
  isRightAnswer = null,
}) => {
  const isWrongAnswer =
    typeof isRightAnswer === 'boolean' && !isRightAnswer && checked

  return (
    <Button
      className={className}
      data-testid="vote-option"
      data-selected={checked}
      data-disabled={disabled}
      data-right-answer={isRightAnswer === true}
      data-wrong-answer={isWrongAnswer}
      onClick={() => !disabled && onChange(value)}
    >
      <Content>{children}</Content>

      {checked && (
        <CheckInCircleIconStyled
          data-right-answer={isRightAnswer === true}
          data-wrong-answer={isWrongAnswer}
        />
      )}
    </Button>
  )
}

const Button = styled.button`
  position: relative;
  display: block;
  padding: 8px 12px;
  border-radius: 16px;
  background: ${colors.white};
  cursor: pointer;
  transition: all 0.2s ease;

  /* Default */
  border: 1px solid ${colors.grey200};

  /* Selected */
  &[data-selected='true'] {
    border-color: ${colors.secondary};
  }

  /* Right answer */
  &[data-right-answer='true'] {
    border-color: ${colors.success};
  }

  /* Wrong answer */
  &[data-wrong-answer='true'] {
    border-color: ${colors.danger};
  }

  /* Disabled */
  &[data-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* Hover states */
  &:hover:not([data-disabled='true']) {
    &[data-selected='true'] {
      border-color: ${colors.borderFocus};
    }

    &:not([data-selected='true']) {
      border-color: ${colors.borderHover};
    }
  }

  /* Active */
  &:active:not([data-disabled='true']) {
    transform: scale(0.98);
  }
`

const Content = styled.div`
  width: 100%;
`

const CheckInCircleIconStyled = styled(CheckInCircleIcon)`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid white;

  /* Default selected state */
  color: ${colors.secondary};

  /* Right answer - green */
  &[data-right-answer='true'] {
    color: ${colors.success};
  }

  /* Wrong answer - red */
  &[data-wrong-answer='true'] {
    color: ${colors.danger};
  }
`
